/** @format */

import React, { useState } from "react";

import { ReactComponent as FileEarmarkWordFill } from "bootstrap-icons/icons/file-earmark-word-fill.svg";
import { ReactComponent as FileEarmarkPDFFill } from "bootstrap-icons/icons/file-earmark-pdf-fill.svg";
import { ReactComponent as FileEarmarkPPTFill } from "bootstrap-icons/icons/file-earmark-ppt-fill.svg";
import { ReactComponent as FileEarmarkExcelFill } from "bootstrap-icons/icons/file-earmark-excel-fill.svg";
import { ReactComponent as FileEarmarkTxtFill } from "bootstrap-icons/icons/file-earmark-text-fill.svg";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash.svg";
import { ReactComponent as Pencil } from "bootstrap-icons/icons/pencil.svg";
import { ReactComponent as Check } from "bootstrap-icons/icons/check-lg.svg";

import { Button, Form } from "react-bootstrap";
import { useLocale } from "../../i18n/hooks";
import { useTranslation } from "react-i18next";
import { Download } from "./addDownload";

import "./DownloadItem.scss";

type Props = {
  index: number;
  download: Download;
  setDownload: (index: number, type: "fileName" | "url", value: string) => void;
  setDownloadToDeleteId: (id: number) => void;
  validated?: boolean;
};

const DownloadItem: React.FC<Props> = ({
  download,
  index,
  setDownload,
  setDownloadToDeleteId,
  validated,
}) => {
  const locale = useLocale();
  const { t: _ } = useTranslation();

  const [showEditView, setShowEditView] = useState<boolean>(true);

  const url =
    typeof download.url === "object" ? download.url[locale] : download.url;
  const filename =
    typeof download.fileName === "object"
      ? download.fileName[locale]
      : download.fileName;

  const icon = (url?: string) => {
    if (url)
      switch (true) {
        case /.pdf/.test(url):
          return (
            <FileEarmarkPDFFill
              aria-label="PDF"
              className="me-3 file-type-icon"
            />
          );
        case /.doc/.test(url):
          return (
            <FileEarmarkWordFill
              aria-label="Word"
              className="me-3 file-type-icon"
            />
          );
        case /.ppt/.test(url):
          return (
            <FileEarmarkPPTFill
              aria-label="PowerPoint"
              className="me-3 file-type-icon"
            />
          );
        case /.xls/.test(url):
          return (
            <FileEarmarkExcelFill
              aria-label="Excel"
              className="me-3 file-type-icon"
            />
          );
      }
    else {
      return <FileEarmarkTxtFill className="me-3 file-type-icon" />;
    }
  };

  const validateUrl = (): boolean => {
    let valid = false;
    const validExtensions: string[] = [
      "pdf",
      "doc",
      "ppt",
      "xls",
      "docx",
      "pptx",
      "xlsx",
    ];

    const extension = url?.split(".").pop();

    if (
      extension &&
      url?.startsWith("https://") &&
      validExtensions.includes(extension)
    ) {
      valid = true;
    }

    validated = valid;
    return valid;
  };

  return (
    <>
      <div className="download-item download-container d-flex align-items-stretch mt-3 mb-0">
        <div
          className="download-details d-flex align-items-center me-2 flex-grow-1"
          key={`download-${index}`}
          aria-label="download-item"
        >
          {!showEditView ? (
            <>
              {icon(url)}
              {!!filename ? (
                <a href={url} target="_blank" rel="noreferrer">
                  {filename}
                </a>
              ) : (
                <a href={url} target="_blank" rel="noreferrer">
                  {url?.split("/").pop()}
                </a>
              )}
            </>
          ) : (
            <div className="w-100 ">
              <div className="d-flex align-items-center">
                <span className="me-3 col-1">{_("Title")}</span>
                <div className="w-100 ">
                  <Form.Control
                    type="text"
                    value={filename ?? ""}
                    onChange={(e) =>
                      setDownload(index, "fileName", e.target.value)
                    }
                    className="rounded-0 rounded-start download-input form-control bg-auto bg-white border"
                  />
                </div>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3 col-1">
                  <span style={{ color: "red" }}>*</span> {_("Link")}
                </span>
                <div className="rounded-left w-100 mt-2">
                  <Form.Control
                    type="text"
                    required
                    isInvalid={validated && !validateUrl()}
                    value={url}
                    onChange={(e) => setDownload(index, "url", e.target.value)}
                    className={`rounded-0 rounded-start download-input form-control bg-auto bg-white border ${validated && !validateUrl() ? "is-invalid" : "valid"}`}
                    pattern="https:\/\/.*\.(pdf|docx|doc|ppt|xls|pptx|xlsx)"
                  />
                  <Form.Check
                    required
                    hidden
                    onChange={() => {}}
                    checked={validateUrl() ?? false}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="button-container">
          <Button
            variant="outline-editing"
            className="delete-download h-100 me-2"
            onClick={() => setShowEditView(!showEditView)}
          >
            {!showEditView ? (
              <Pencil style={{ width: "20px", height: "20px" }} />
            ) : (
              <Check style={{ width: "20px", height: "20px" }} />
            )}
          </Button>
          <Trash
            style={{ width: "20px", height: "20px" }}
            onClick={() => setDownloadToDeleteId(index)}
            className="h-100 element-action mx-2"
          />
        </div>
      </div>
      {!validated && (
        <div className="invalid-feedback d-block text-end me-5 pe-5">
          {!!!url
            ? _("Please enter a link.")
            : !validateUrl()
              ? "Link must be secure and be an acceptable extension (.pdf, .xls, .xlsx, .ppt, .pptx, .doc, .docx)"
              : ""}
        </div>
      )}
    </>
  );
};

export default DownloadItem;
